import { path, pathOr, prop, propOr } from 'ramda';

import { fragments } from 'config/dictionary';

const BLUE_CLIENT_ID = process.env.REACT_APP_BLUE_CLIENT_ID;
const POST_OFFICE_CLIENT_ID = process.env.REACT_APP_POST_OFFICE_CLIENT_ID;
const COOP_PRODUCT_ID = process.env.REACT_APP_COOP_PRODUCT_ID;

export const getSenderName = huggg => {
  const sender = propOr({}, 'sender', huggg);

  if (sender.send_as_name) {
    return sender.send_as_name;
  }

  const name = `${propOr('', 'first_name', sender)} ${propOr(
    '',
    'surname',
    sender
  )}`;
  return name;
};

/**
 * The displayed label for a Huggg varies whether it is consolidated or not.
 * Return the correct generated string based on this.
 *
 * @param {Object} huggg
 * @returns {String}
 */
export const getExpandedLabel = huggg => {
  const purchase = prop('purchase', huggg);
  const brand = path(['purchase', 'brand'], huggg);
  const label = propOr('huggg', 'label', purchase);
  const name =
    brand && isConsolidated(huggg)
      ? fragments.consolidatedName
      : prop('name', brand);

  return name ? `${label} at ${name}` : label;
};

/**
 * Has the provided huggg already been redeemed
 *
 * @param {Object} huggg
 * @returns {Boolean}
 */
export const isRedeemed = huggg => Boolean(huggg.redeemed_id);

/**
 * Is the provided huggg a super huggg. A super huggg is a single huggg that
 * can be redeemed multiple times.
 *
 * @param {Object} huggg
 * @returns {Boolean}
 */
export const isSuperHuggg = huggg => huggg.isSuperhuggg;

/**
 * Is the provided huggg a consolidated huggg. This is buried deep in the data.
 *
 * @param {Object} huggg
 * @returns {Boolean}
 */
export const isConsolidated = ({ purchase: { brand } }) =>
  Boolean(brand.consolidated);

export const getBrandName = huggg =>
  pathOr(null, ['purchase', 'brand_name'], huggg);

export const getCoverImageUrl = huggg => prop('cover_image_url', huggg);

const transformReaction = reaction => {
  if (!reaction) {
    return undefined;
  }

  return {
    reaction: {
      loadingEmoji: false,
      loadingComment: false,
      loadingEmojiError: '',
      loadingCommentError: '',
      emoji: reaction?.emoji || '',
      name: reaction?.name || '',
      message: reaction?.message || '',
      id: reaction?.id || undefined
    }
  };
};

const getAllSegments = huggg => {
  const items = pathOr(null, ['purchase', 'items'], huggg);
  return items || pathOr([], ['purchase', 'segments'], huggg);
};

const getCustomSegments = huggg => {
  const list = pathOr(null, ['meta', 'gwcSegments'], huggg);

  // @later we can use the images on the prop here, but I just want to get
  // this out now!
  return list && Array.isArray(list) && list.length
    ? list.map(({ id }) => id)
    : null;
};

/**
 * There is optional meta data that can be passed through for the GWC
 * which should filter the displayed segments.
 *
 * If this fails to match valid choices correctly, we'll fallback to
 * the original segment behaviour.
 */
export const getSegments = huggg => {
  const includeList = getCustomSegments(huggg);
  const allSegments = getAllSegments(huggg);

  if (!includeList) {
    return allSegments;
  }

  const filtered = allSegments.filter(segment =>
    includeList.includes(segment.id)
  );

  return filtered.length ? filtered : allSegments;
};

export const transformHuggg = huggg => {
  return {
    huggg: {
      ...huggg,
      ...(huggg && huggg.purchase
        ? {
            purchase: {
              ...huggg.purchase,
              segments: getSegments(huggg)
            }
          }
        : {})
    },
    brand: pathOr({}, ['purchase', 'brand'], huggg),
    locations: pathOr(null, ['purchase', 'brand', 'locations'], huggg),
    message: propOr({}, 'message', huggg),
    ...transformReaction(propOr(undefined, 'reaction', huggg))
  };
};

export const redeemInstructions = huggg =>
  path(['purchase', 'redemption_instructions'], huggg);

export const getProductName = huggg =>
  pathOr('huggg', ['purchase', 'label'], huggg);

export const getBasePathname = huggg => {
  if (huggg.isSuperhuggg) {
    return `/super/${huggg.shortcode}`;
  } else if (huggg.is_test) {
    return `/test/${huggg.shortcode}`;
  } else {
    return `/${huggg.shortcode}`;
  }
};

export const getVoucherAmount = ({
  pence,
  isGiftCard,
  variantLabel,
  alwaysShowDecimal = false
}) => {
  if (variantLabel && isGiftCard) {
    // Remove the last word from the variant label
    // eg. €100 Germany -> €100, 200 BGN Bulgaria -> 200 BGN
    return variantLabel.replace(/\s+\S+$/, '');
  }

  if (pence) {
    const pounds = pence / 100;

    if (alwaysShowDecimal || pence % 100 !== 0) {
      return `£${pounds.toFixed(2)}`;
    }

    return `£${pounds.toFixed(0)}`;
  }
  return '';
};

export const getHugggTypeText = huggg => {
  const segments = pathOr([], ['purchase', 'segments'], huggg);
  const isPin4 =
    segments.length === 1 &&
    pathOr(null, [0, 'voucherType'], segments) === 'pin4';

  const purchaseClientId = pathOr(null, ['purchase_client_id'], huggg);
  const isDeliverable = huggg.purchase?.behavioural_type === 'delivered';
  const isVouchers = [BLUE_CLIENT_ID, POST_OFFICE_CLIENT_ID].includes(
    purchaseClientId
  );

  if (isVouchers) {
    if (isDeliverable) {
      return 'an item';
    }
    return `a voucher`;
  }

  return isPin4 ? 'a cash voucher' : 'a gift!';
};

export const getHugggTitle = huggg => {
  const purchaseClientId = pathOr(null, ['purchase_client_id'], huggg);
  const isDeliverable = huggg.purchase?.behavioural_type === 'delivered';
  const isGiftCard = huggg.purchase?.behavioural_type === 'gift-card';
  const variantLabel = huggg.purchase?.variant_label;
  const isVouchers = [BLUE_CLIENT_ID, POST_OFFICE_CLIENT_ID].includes(
    purchaseClientId
  );

  const voucherAmount = getVoucherAmount({
    pence: huggg.purchase?.sale_price,
    isGiftCard,
    variantLabel
  });

  if (
    (isVouchers && !isDeliverable && voucherAmount) ||
    (!isVouchers && isGiftCard && voucherAmount)
  ) {
    return huggg?.purchase?.label + ' - ' + voucherAmount;
  }

  return huggg?.purchase?.label;
};

export const makeTitle = huggg => {
  const senderName = huggg.from_name;
  const type = getHugggTypeText(huggg);

  return senderName
    ? `${senderName} has sent you ${type}`
    : `You've been sent ${type}`;
};

export const makeReactionTitle = huggg => `Say thanks to ${huggg.from_name}`;
export const makeLandingTitle = huggg => {
  const type = getHugggTypeText(huggg);

  return `${huggg.from_name} sent you ${type}`;
};

/**
 * Returns true if every segment is a gift-card
 */
export const isGiftCard = huggg => {
  const segments = getSegments(huggg);
  return (
    segments &&
    segments.length &&
    segments.some(segment =>
      ['tillo', 'wegift'].includes(propOr(null, 'voucherType', segment))
    )
  );
};

/**
 * This is required as stored vouchers usually just render a QR code and so have no information,
 * about where they can be used.
 * Ideally, this should end up as a flag in the CMS at some point in the near future, but it'll do for now.
 */
export const shouldUseRedeemedSegmentInformation = huggg => {
  return !!(huggg.redeemedSegment?.name && huggg.redeemedSegment?.images?.[0]);
  // return (
  //   huggg.vouchers?.find(({ type }) => ['qr', 'barcode'].includes(type)) ||
  //   huggg.redeemedSegment?.voucherType === 'stored'
  // );
};

export const getRedeemedSegmentImage = huggg => {
  return huggg.redeemedSegment?.images?.[0];
};

export const getRedeemedSegmentName = huggg => {
  return huggg.redeemedSegment?.name;
};

export const needsCustomImage = huggg =>
  Boolean(getCustomSegments(huggg)) && !isGiftCard(huggg);

export const getSegmentImages = huggg =>
  getSegments(huggg)
    ?.map(segment => segment.images[0] ?? null)
    .filter(Boolean) ?? [];

export const getHugggImage = huggg => {
  return (
    huggg?.purchase?.images?.redemptionCover?.[0] ??
    huggg?.purchase?.images?.main?.[0] ??
    huggg?.purchase?.image_url ??
    huggg?.image_url
  );
};

export const isCoopVoucher = huggg => {
  return huggg?.purchase_id && huggg?.purchase_id === COOP_PRODUCT_ID;
};
